import { Building2Icon, Plus, SearchLoop } from 'assets/icons';
import Button from 'components/button';
import PickOption from 'components/record/PickOption';
import StyledInput from 'components/styledInput/StyledInput';
import { foldText } from 'helper/foldText';
import useText from 'hooks/useText';
import { useEffect, useRef, useState } from 'react';

/**
 * « Créer une mission » on a group: the button opens the group's companies
 * under a search bar, in a list of limited height that scrolls. Picking one
 * hands it to `onPick`. A click outside or Escape closes it. The question
 * « Pour quelle entreprise ? » is only read out (the dialog's label).
 * `companies`: { id, name, detail }.
 */
const CompanyPickMenu = ({ label, companies, onPick }) => {
  const { text } = useText('record');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    inputRef.current?.focus();
    const closeOutside = (event) => {
      if (!ref.current?.contains(event.target)) setOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', closeOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  const toggle = () => {
    setSearch('');
    setOpen((value) => !value);
  };
  const query = foldText(search.trim());
  const shown = companies
    .filter((company) => !query || foldText(company.name).includes(query) || foldText(company.detail).includes(query))
    .sort((a, b) => a.name.localeCompare(b.name, 'fr'));

  return (
    <div ref={ref} className="relative shrink-0">
      <Button color="SECONDARY" size="SM" aria-expanded={open} aria-haspopup="dialog" onClick={toggle}>
        <Plus wh={18} color="currentColor" />
        <span>{label}</span>
      </Button>
      {open && (
        <div
          role="dialog"
          aria-label={text('pickCompanyTitle')}
          className="absolute right-0 top-full z-30 mt-2 flex w-80 flex-col gap-3 rounded-xl border border-gray-100 bg-white p-3 shadow-lg motion-safe:animate-contentIn"
        >
          <StyledInput
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={text('pickCompanySearch')}
            imgBack={<SearchLoop wh={16} color="#6B7280" />}
            inputRef={inputRef}
          />
          <div className="-mx-1 max-h-72 overflow-y-auto">
            {!shown.length && <p className="px-2 py-3 text-sm text-gray-500">{text('pickCompanyEmpty')}</p>}
            {shown.map((company) => (
              <PickOption
                key={company.id}
                icon={Building2Icon}
                name={company.name}
                detail={company.detail}
                uppercase
                onClick={() => {
                  setOpen(false);
                  onPick(company);
                }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyPickMenu;
